import React, { useState, useEffect } from 'react';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { useLang } from '../LangContext';
import { statusText } from '../i18n';
import { AccessDenied, canAccess, canSeeMoney, isOrderInScope } from '../adminAccess';
import DatabaseError from '../components/DatabaseError';

const STATUS_COLORS = {
  pending: '#F59E0B', confirmed: '#3B82F6', assigned: '#8B5CF6',
  accepted: '#6366F1', worker_on_route: '#0EA5E9', in_progress: '#F97316',
  before_photos_uploaded: '#EC4899', after_photos_uploaded: '#14B8A6',
  completed: '#10B981', cancelled: '#EF4444',
};

export default function WorkersPage({ user }) {
  const { tr, lang } = useLang();
  const [workers, setWorkers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedWorker, setExpandedWorker] = useState(null);
  const [msg, setMsg] = useState('');
  const [search, setSearch] = useState('');
  const [dbError, setDbError] = useState('');

  useEffect(() => {
    const unsub1 = onSnapshot(
      query(collection(db, 'users'), where('role', '==', 'worker')),
      snap => {
        setDbError('');
        setWorkers(snap.docs.map(d => ({ id: d.id, ...d.data() }))
          .filter(worker => (worker.approvalStatus || 'approved') === 'approved'));
      },
      err => {
        console.error(err);
        setDbError(err?.code === 'permission-denied'
          ? 'Permission denied. Please log out and log in again as the super admin.'
          : (err?.message || 'Unable to read workers.'));
      }
    );
    const unsub2 = onSnapshot(collection(db, 'orders'), snap =>
      setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })))
    , err => {
      console.error(err);
      setDbError(err?.code === 'permission-denied'
        ? 'Permission denied. Please log out and log in again as the super admin.'
        : (err?.message || 'Unable to read worker orders.'));
    }
    );
    return () => { unsub1(); unsub2(); };
  }, []);

  function flash(m) { setMsg(m); setTimeout(() => setMsg(''), 4000); }

  if (!canAccess(user, 'workers')) return <AccessDenied />;

  const showMoney = canSeeMoney(user);

  function getWorkerOrders(w) {
    const uid = w.uid || w.id;
    return orders.filter(o => o.workerId === uid && isOrderInScope(user, o));
  }

  const filtered = workers.filter(w =>
    !search || w.name?.toLowerCase().includes(search.toLowerCase()) || w.email?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="page-shell">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div><h1 className="page-title">{tr('workers')}</h1><p className="page-description">Manage approved workers, access, and assigned work.</p></div>
        <button onClick={() => setShowAddModal(true)} className="btn-primary">{tr('addWorker')}</button>
      </div>

      {msg && <div className="mb-4 px-4 py-2 bg-green-50 border border-green-200 text-green-700 rounded-xl text-sm">{msg}</div>}
      <DatabaseError message={dbError} />

      <input className="input-field w-full mb-6" placeholder={tr('search')} value={search} onChange={e => setSearch(e.target.value)} />

      <div className="space-y-3">
        {filtered.map(w => {
          const workerOrders = getWorkerOrders(w);
          const completed = workerOrders.filter(o => o.status === 'completed').length;
          const active = workerOrders.filter(o => o.status !== 'completed' && o.status !== 'cancelled' && o.workerId).length;
          const expanded = expandedWorker === w.id;

          return (
            <div key={w.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              {/* Worker header */}
              <div className="flex flex-col xl:flex-row xl:items-center gap-3 sm:gap-4 p-4 sm:p-5">
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center text-2xl font-bold text-primary">
                  {(w.name || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-gray-900">{w.name || '—'}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${w.status === 'blocked' ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-700'}`}>
                      {w.status === 'blocked' ? tr('blocked') : tr('active')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">{w.email}</p>
                  <p className="text-sm text-gray-400">{w.phone}</p>
                </div>
                {/* Stats */}
                <div className="flex gap-4 text-center justify-around w-full sm:w-auto sm:justify-start sm:mr-2">
                  <div>
                    <p className="text-lg font-bold text-green-600">{completed}</p>
                    <p className="text-xs text-gray-400">{tr('completed')}</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-orange-500">{active}</p>
                    <p className="text-xs text-gray-400">{tr('inProgress')}</p>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-amber-500">{w.rating?.toFixed(1) || '—'}</p>
                    <p className="text-xs text-gray-400">⭐ {tr('rating')}</p>
                  </div>
                </div>
                {/* Actions */}
                <div className="flex w-full flex-wrap gap-2 xl:w-auto xl:justify-end">
                  <button
                    onClick={() => setExpandedWorker(expanded ? null : w.id)}
                    className="btn-secondary text-sm"
                  >
                    {expanded ? '▲' : '▼'} {tr('assignedOrders')} ({workerOrders.length})
                  </button>
                  <button
                    onClick={async () => {
                      await httpsCallable(functions, 'manageUserAccess')({
                        uid: w.id,
                        action: w.status === 'blocked' || w.status === 'disabled' ? 'reenable' : 'disable',
                      });
                    }}
                    className={`text-sm px-3 py-1.5 rounded-lg font-medium transition ${
                      w.status === 'blocked'
                        ? 'bg-green-50 text-green-600 hover:bg-green-100'
                        : 'bg-red-50 text-red-600 hover:bg-red-100'
                    }`}
                  >
                    {w.status === 'blocked' ? tr('activate') : tr('block')}
                  </button>
                </div>
              </div>

              {/* Expanded orders */}
              {expanded && (
                <div className="border-t border-gray-100 bg-gray-50 p-4">
                  <p className="font-semibold text-sm text-gray-700 mb-3">{tr('assignedOrders')}</p>
                  {workerOrders.length === 0 ? (
                    <p className="text-gray-400 text-sm">{tr('noData')}</p>
                  ) : (
                    <div className="space-y-2">
                      {workerOrders.map(o => (
                        <div key={o.id} className="bg-white rounded-xl border border-gray-100 px-4 py-3 flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm text-gray-900">{o.deceased?.name || '—'}</p>
                            <p className="text-xs text-gray-500">{o.cemeteryName || '—'} · {o.orderNumber}</p>
                          </div>
                          <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: (STATUS_COLORS[o.status] || '#6B7280') + '20', color: STATUS_COLORS[o.status] || '#6B7280' }}>
                            {statusText(lang, o.status)}
                          </span>
                          <span className="font-semibold text-primary text-sm">{showMoney ? `${o.totalAmount?.toFixed(2)} ₼` : 'Hidden'}</span>
                          <span className="text-xs text-gray-400">
                            {o.createdAt?.toDate ? o.createdAt.toDate().toLocaleDateString() : '—'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="text-center text-gray-400 py-20">{tr('noData')}</div>
        )}
      </div>

      {showAddModal && (
        <AddWorkerModal
          tr={tr}
          onClose={() => setShowAddModal(false)}
          onAdded={createdEmail => flash(`${tr('workerAdded')} Login: ${createdEmail}`)}
        />
      )}
    </div>
  );
}

function AddWorkerModal({ tr, onClose, onAdded }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function submit(e) {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const normalizedEmail = email.trim().toLowerCase();
      await httpsCallable(functions, 'createManagedUser')({
        name: name.trim(),
        email: normalizedEmail,
        phone: phone.trim(),
        role: 'worker',
        password,
      });
      onAdded(normalizedEmail); onClose();
    } catch (err) {
      setError(err?.message || 'Unable to create worker.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">{tr('addWorker')}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">×</button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          {error && (
            <div className="text-red-700 text-sm bg-red-50 px-3 py-3 rounded-xl">
              <p>{error}</p>
            </div>
          )}
          {[
            { label: tr('fullName'), val: name, set: setName, ph: 'Ali Rəsulov', req: true },
            { label: tr('email'), val: email, set: setEmail, ph: 'ali@example.com', req: true, type: 'email' },
            { label: tr('phone'), val: phone, set: setPhone, ph: '+994501234567' },
            { label: tr('password'), val: password, set: setPassword, ph: '••••••••', req: true, type: 'password' },
          ].map(f => (
            <div key={f.label}>
              <label className="block text-xs font-semibold text-gray-600 mb-1">{f.label}</label>
              <input className="input-field w-full" type={f.type || 'text'} value={f.val} onChange={e => f.set(e.target.value)} placeholder={f.ph} required={f.req} />
            </div>
          ))}
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 btn-secondary">{tr('cancel')}</button>
            <button type="submit" disabled={saving} className="flex-1 btn-primary">{saving ? '...' : tr('addWorker')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
